// 1.3.73: MVP 多租户架构 - 组织服务
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { logger } from '../utils/logger';
import type {
  Organization,
  OrganizationMember,
  UserOrganization,
  OrganizationDetails,
  OrganizationKnowledgeBase,
  OrganizationApiResult,
  MemberRole,
} from '../types/organization';

export interface ServiceError {
  message: string;
  code?: string;
}

// ============================================
// 组织列表和详情
// ============================================

/**
 * 获取当前用户的所有组织列表
 */
export async function getUserOrganizations(): Promise<{
  organizations: UserOrganization[];
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { organizations: [], error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('get_user_organizations');

    if (error) {
      logger.error('Error getting user organizations:', error);
      return {
        organizations: [],
        error: { message: error.message, code: error.code },
      };
    }

    return { organizations: data || [], error: null };
  } catch (error: any) {
    logger.error('Error getting user organizations:', error);
    return {
      organizations: [],
      error: { message: error.message || '获取组织列表失败' },
    };
  }
}

/**
 * 获取组织详情（包含成员列表）
 */
export async function getOrganizationDetails(
  organizationId: string
): Promise<{
  details: OrganizationDetails | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { details: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('get_organization_details', {
      org_id: organizationId,
    });

    if (error) {
      logger.error('Error getting organization details:', error);
      return {
        details: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { details: data as OrganizationDetails, error: null };
  } catch (error: any) {
    logger.error('Error getting organization details:', error);
    return {
      details: null,
      error: { message: error.message || '获取组织详情失败' },
    };
  }
}

/**
 * 获取用户当前（默认）组织
 * 1.4.5: 支持通过 localStorage 切换当前组织
 */
export async function getCurrentOrganization(): Promise<{
  organization: UserOrganization | null;
  error: ServiceError | null;
}> {
  const { organizations, error } = await getUserOrganizations();
  
  if (error) {
    return { organization: null, error };
  }

  // 1.4.5: 优先使用 localStorage 中存储的当前组织 ID
  const storedOrgId = localStorage.getItem('currentOrganizationId');
  if (storedOrgId) {
    const storedOrg = organizations.find(org => org.id === storedOrgId);
    if (storedOrg) {
      return { organization: storedOrg, error: null };
    }
    // 如果存储的组织 ID 无效（用户可能已被移除），清除它
    localStorage.removeItem('currentOrganizationId');
  }

  // 返回标记为 is_current 的组织，或第一个组织
  const currentOrg = organizations.find(org => org.is_current) || organizations[0] || null;
  
  return { organization: currentOrg, error: null };
}

/**
 * 设置当前活动组织
 * 1.4.5: 用于切换用户的当前组织
 */
export function setCurrentOrganization(organizationId: string): void {
  localStorage.setItem('currentOrganizationId', organizationId);
}

/**
 * 清除当前组织设置
 * 1.4.5: 恢复为默认组织选择逻辑
 */
export function clearCurrentOrganization(): void {
  localStorage.removeItem('currentOrganizationId');
}

// ============================================
// 成员管理
// ============================================

/**
 * 邀请成员加入组织
 */
export async function inviteMember(
  organizationId: string,
  email: string,
  role: MemberRole = 'member'
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('invite_member_to_organization', {
      org_id: organizationId,
      member_email: email,
      member_role: role,
    });

    if (error) {
      logger.error('Error inviting member:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error inviting member:', error);
    return {
      result: null,
      error: { message: error.message || '邀请成员失败' },
    };
  }
}

/**
 * 移除组织成员
 */
export async function removeMember(
  organizationId: string,
  userId: string
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('remove_organization_member', {
      org_id: organizationId,
      member_user_id: userId,
    });

    if (error) {
      logger.error('Error removing member:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error removing member:', error);
    return {
      result: null,
      error: { message: error.message || '移除成员失败' },
    };
  }
}

/**
 * 更新成员角色
 */
export async function updateMemberRole(
  organizationId: string,
  userId: string,
  newRole: MemberRole
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('update_member_role', {
      org_id: organizationId,
      member_user_id: userId,
      new_role: newRole,
    });

    if (error) {
      logger.error('Error updating member role:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error updating member role:', error);
    return {
      result: null,
      error: { message: error.message || '更新角色失败' },
    };
  }
}

// ============================================
// 组织创建和管理
// ============================================

/**
 * 创建企业组织（用于升级到 Standard 套餐）
 */
export async function createBusinessOrganization(
  name: string,
  plan: 'individual' | 'standard' = 'standard'
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('create_business_organization', {
      org_name: name,
      initial_plan: plan,
    });

    if (error) {
      logger.error('Error creating organization:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error creating organization:', error);
    return {
      result: null,
      error: { message: error.message || '创建组织失败' },
    };
  }
}

/**
 * 更新组织信息
 */
export async function updateOrganization(
  organizationId: string,
  updates: Partial<Pick<Organization, 'name'>>
): Promise<{
  organization: Organization | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { organization: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase
      .from('organizations')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', organizationId)
      .select()
      .single();

    if (error) {
      logger.error('Error updating organization:', error);
      return {
        organization: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { organization: data as Organization, error: null };
  } catch (error: any) {
    logger.error('Error updating organization:', error);
    return {
      organization: null,
      error: { message: error.message || '更新组织失败' },
    };
  }
}

// ============================================
// 知识库相关
// ============================================

/**
 * 获取组织的知识库列表
 */
export async function getOrganizationKnowledgeBases(
  organizationId: string
): Promise<{
  knowledgeBases: OrganizationKnowledgeBase[];
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { knowledgeBases: [], error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('get_organization_knowledge_bases', {
      org_id: organizationId,
    });

    if (error) {
      logger.error('Error getting organization knowledge bases:', error);
      return {
        knowledgeBases: [],
        error: { message: error.message, code: error.code },
      };
    }

    return { knowledgeBases: data || [], error: null };
  } catch (error: any) {
    logger.error('Error getting organization knowledge bases:', error);
    return {
      knowledgeBases: [],
      error: { message: error.message || '获取知识库列表失败' },
    };
  }
}

/**
 * 切换知识库共享状态
 */
export async function toggleKnowledgeBaseSharing(
  knowledgeBaseId: string,
  isShared: boolean
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('toggle_knowledge_base_sharing', {
      kb_id: knowledgeBaseId,
      share_status: isShared,
    });

    if (error) {
      logger.error('Error toggling knowledge base sharing:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error toggling knowledge base sharing:', error);
    return {
      result: null,
      error: { message: error.message || '更新共享状态失败' },
    };
  }
}

/**
 * 在组织内创建知识库
 */
export async function createKnowledgeBaseInOrg(
  organizationId: string,
  name: string,
  description?: string,
  difyDatasetId?: string,
  isShared: boolean = false
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('create_knowledge_base_in_org', {
      org_id: organizationId,
      kb_name: name,
      kb_description: description,
      kb_dify_dataset_id: difyDatasetId,
      kb_is_shared: isShared,
    });

    if (error) {
      logger.error('Error creating knowledge base:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error creating knowledge base:', error);
    return {
      result: null,
      error: { message: error.message || '创建知识库失败' },
    };
  }
}

// ============================================
// 权限检查辅助函数
// ============================================

/**
 * 检查用户是否是组织成员
 */
export async function isOrganizationMember(
  organizationId: string
): Promise<boolean> {
  const { organizations } = await getUserOrganizations();
  return organizations.some(org => org.id === organizationId);
}

/**
 * 检查用户是否是组织 Owner
 */
export async function isOrganizationOwner(
  organizationId: string
): Promise<boolean> {
  const { organizations } = await getUserOrganizations();
  const org = organizations.find(o => o.id === organizationId);
  return org?.role === 'owner';
}

/**
 * 获取用户在指定组织中的角色
 */
export async function getUserRoleInOrganization(
  organizationId: string
): Promise<MemberRole | null> {
  const { organizations } = await getUserOrganizations();
  const org = organizations.find(o => o.id === organizationId);
  return org?.role || null;
}

// ============================================
// Onboarding 相关
// ============================================

export interface OnboardingStatus {
  needs_onboarding: boolean;
  has_organization: boolean;
  completed: boolean;
  selected_type?: 'personal' | 'business';
}

/**
 * 获取用户 Onboarding 状态
 */
export async function getOnboardingStatus(): Promise<{
  status: OnboardingStatus | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { status: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('get_user_onboarding_status');

    if (error) {
      logger.error('Error getting onboarding status:', error);
      return {
        status: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { status: data as OnboardingStatus, error: null };
  } catch (error: any) {
    logger.error('Error getting onboarding status:', error);
    return {
      status: null,
      error: { message: error.message || '获取 Onboarding 状态失败' },
    };
  }
}

/**
 * 完成个人 Onboarding
 */
export async function completePersonalOnboarding(): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('complete_personal_onboarding');

    if (error) {
      logger.error('Error completing personal onboarding:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error completing personal onboarding:', error);
    return {
      result: null,
      error: { message: error.message || '创建个人工作区失败' },
    };
  }
}

/**
 * 完成企业 Onboarding
 */
export async function completeBusinessOnboarding(
  companyName: string,
  companySize: string,
  industry?: string,
  jobTitle?: string
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('complete_business_onboarding', {
      company_name: companyName,
      company_size: companySize,
      company_industry: industry,
      user_job_title: jobTitle,
    });

    if (error) {
      logger.error('Error completing business onboarding:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error completing business onboarding:', error);
    return {
      result: null,
      error: { message: error.message || '创建企业组织失败' },
    };
  }
}

/**
 * 个人升级为企业
 */
export async function upgradeToBusiness(
  companyName: string,
  companySize: string,
  industry?: string
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('upgrade_to_business', {
      company_name: companyName,
      company_size: companySize,
      company_industry: industry,
    });

    if (error) {
      logger.error('Error upgrading to business:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error upgrading to business:', error);
    return {
      result: null,
      error: { message: error.message || '升级失败' },
    };
  }
}

// ============================================
// 邀请链接相关
// ============================================

// 1.5.7: 添加 organization_id 字段，用于验证用户是否已是目标组织成员
export interface InvitationInfo {
  valid: boolean;
  error?: string;
  organization_id?: string;
  organization_name?: string;
  organization_slug?: string;
  role?: MemberRole;
  expires_at?: string;
  email_restricted?: boolean;
}

/**
 * 创建邀请链接
 * 1.5.5: 添加席位配额检查错误处理
 */
export async function createInvitationLink(
  organizationId: string,
  email?: string,
  role: MemberRole = 'member',
  validDays: number = 7,
  maxUses?: number
): Promise<{
  result: { invitation_id: string; invite_token: string; expires_at: string } | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('create_invitation_link', {
      org_id: organizationId,
      invite_email: email,
      invite_role: role,
      valid_days: validDays,
      max_uses_count: maxUses,
    });

    if (error) {
      logger.error('Error creating invitation:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    // 1.5.5: 检查后端返回的业务错误（席位配额等）
    if (data && data.success === false) {
      logger.error('Error creating invitation:', data.message);
      return {
        result: null,
        error: { 
          message: data.message || '创建邀请链接失败', 
          code: data.error 
        },
      };
    }

    return { result: data, error: null };
  } catch (error: any) {
    logger.error('Error creating invitation:', error);
    return {
      result: null,
      error: { message: error.message || '创建邀请链接失败' },
    };
  }
}

/**
 * 检查邀请链接有效性
 */
export async function checkInvitation(
  token: string
): Promise<{
  info: InvitationInfo | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { info: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('check_invitation', {
      token,
    });

    if (error) {
      logger.error('Error checking invitation:', error);
      return {
        info: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { info: data as InvitationInfo, error: null };
  } catch (error: any) {
    logger.error('Error checking invitation:', error);
    return {
      info: null,
      error: { message: error.message || '检查邀请链接失败' },
    };
  }
}

/**
 * 接受邀请加入组织
 */
export async function acceptInvitation(
  token: string
): Promise<{
  result: OrganizationApiResult | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('accept_invitation', {
      token,
    });

    if (error) {
      logger.error('Error accepting invitation:', error);
      return {
        result: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { result: data as OrganizationApiResult, error: null };
  } catch (error: any) {
    logger.error('Error accepting invitation:', error);
    return {
      result: null,
      error: { message: error.message || '接受邀请失败' },
    };
  }
}

/**
 * 获取组织的邀请列表
 */
export async function getOrganizationInvitations(
  organizationId: string
): Promise<{
  invitations: any[];
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { invitations: [], error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase
      .from('organization_invitations')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error getting invitations:', error);
      return {
        invitations: [],
        error: { message: error.message, code: error.code },
      };
    }

    return { invitations: data || [], error: null };
  } catch (error: any) {
    logger.error('Error getting invitations:', error);
    return {
      invitations: [],
      error: { message: error.message || '获取邀请列表失败' },
    };
  }
}

/**
 * 取消邀请
 */
export async function cancelInvitation(
  invitationId: string
): Promise<{
  success: boolean;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { success: false, error: { message: 'Supabase not available' } };
  }

  try {
    const { error } = await supabase
      .from('organization_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId);

    if (error) {
      logger.error('Error cancelling invitation:', error);
      return {
        success: false,
        error: { message: error.message, code: error.code },
      };
    }

    return { success: true, error: null };
  } catch (error: any) {
    logger.error('Error cancelling invitation:', error);
    return {
      success: false,
      error: { message: error.message || '取消邀请失败' },
    };
  }
}

// ============================================
// 1.4.5: 邀请邮件发送
// ============================================

/**
 * 发送邀请邮件
 * 本地开发使用 Inbucket，生产环境使用 Resend
 * 1.5.18: 支持多语言邮件
 */
export async function sendInvitationEmail(params: {
  email: string;
  organizationName: string;
  inviterEmail: string;
  role: MemberRole;
  inviteLink: string;
  locale?: string; // 1.5.18: 邮件语言
}): Promise<{
  success: boolean;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { success: false, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.functions.invoke('send-invite-email', {
      body: params,
    });

    if (error) {
      logger.error('Error sending invitation email:', error);
      return {
        success: false,
        error: { message: error.message || '发送邀请邮件失败' },
      };
    }

    if (data?.error) {
      return {
        success: false,
        error: { message: data.error },
      };
    }

    return { success: true, error: null };
  } catch (error: any) {
    logger.error('Error sending invitation email:', error);
    return {
      success: false,
      error: { message: error.message || '发送邀请邮件失败' },
    };
  }
}
